/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import AppTable from 'components/appTable';
import Empty from 'components/empty';
import PanelBoxLayout from 'components/layout/panelBox';
import Value from 'components/value';
import { MISSING } from 'common/helpers/format';
import { useActions } from 'common/hooks/useActions';

import SurveyAnswerCell from './lib/answer';

import * as S from './styles';
import * as T from 'common/types';
import { useParams } from 'react-router';

interface IProps {
  data: T.Pages.SurveyResult;
  loading: boolean;
}

/** Two fixed columns, then one per question. */
const ID_WIDTH = 120;
const NAME_WIDTH = 200;
/** What a free-text answer needs before it stops being worth reading. */
const ANSWER_WIDTH = 240;

const ResultsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 20h18" />
    <path d="M7 20V10M12 20V4M17 20v-7" />
  </svg>
);

export default function SurveyShowTable({ data, loading }: IProps) {
  const params = useParams();

  const { surveysResultRequestAction } = useActions();
  const [meta] = useState({
    page: 1,
    page_size: 10,
    search: '',
    type: '',
    id: params.id
  });

  const fetchData = () => {
    surveysResultRequestAction(meta);
  };

  useEffect(() => {
    fetchData();
  }, [meta]);

  /*
   * The one genuinely dynamic header list in the product: two fixed columns
   * and then one per question on the survey. Both halves of AppTable's
   * contract are derived from the same sorted list, so a header and its
   * renderer cannot drift apart.
   *
   * This screen used to hand-roll its own `<table>` underneath a commented-out
   * AppTable call — the last pre-redesign table in `src/`, with 14px cells,
   * its own colours and a private white loading scrim.
   */
  const questions = [...(data?.questions || [])].sort((q1, q2) => q1.id - q2.id);

  const headers = [
    { id: 'userId', key: 'userId', title: 'Employee ID', dir: 'left' as const, width: ID_WIDTH },
    { id: 'name', key: 'name', title: 'User', dir: 'left' as const, width: NAME_WIDTH },
    ...questions.map((question: T.Pages.SurveyResultQuestion) => ({
      id: `q${question.id}`,
      key: `q${question.id}`,
      title: question.title,
      dir: 'left' as const,
      width: ANSWER_WIDTH
    }))
  ];

  const answerFields = questions.reduce((fields: Record<string, any>, question: T.Pages.SurveyResultQuestion) => {
    fields[`q${question.id}`] = (item: T.Pages.SurveyResultAnswer) => {
      /*
       * Matched on `questionId`, not on position. The old body sorted each
       * respondent's answers and laid them out in order, which lines up only
       * while everybody has answered everything — one skipped question and
       * every later answer on that row sat under the wrong heading.
       */
      const answer = item?.answers?.find((entry) => entry?.questionId === question.id);
      return (
        <S.Div className="text-left">
          <SurveyAnswerCell answer={answer?.answer} question={question.title} respondent={item?.name} />
        </S.Div>
      );
    };
    return fields;
  }, {});

  return (
    <S.Content>
      <PanelBoxLayout
        className="flush"
        contentStyle={{ padding: 0 }}
        title="Responses"
        hint={data?.answers?.length ? `${data.answers.length} on this page` : undefined}
      >
        <AppTable
          headers={headers}
          loading={loading}
          data={data?.answers || []}
          /* Two fixed columns plus a question apiece; past four questions the
             table outgrows the card and AppTable's own container scrolls it. */
          style={{ minWidth: ID_WIDTH + NAME_WIDTH + questions.length * ANSWER_WIDTH }}
          empty={
            <Empty
              icon={<ResultsIcon />}
              title="No responses yet"
              text="Answers appear here as employees complete the survey."
            />
          }
          altanfeethiFields={{
            userId: (item: T.Pages.SurveyResultAnswer) => (
              <S.Div className="text-left">
                <Value mono>{item?.userId}</Value>
              </S.Div>
            ),
            name: (item: T.Pages.SurveyResultAnswer) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.provided}>{item?.name}</Value>
              </S.Div>
            ),
            ...answerFields
          }}
        />
      </PanelBoxLayout>
    </S.Content>
  );
}
